import { useQuery } from '@tanstack/react-query'

import { queryFavorites, QUERY_KEY } from '~/app/features/Gallery/queries/queryFavorites'
import { useSelector } from 'react-redux'
import { WES_TENANT } from '~/app/constants'
import { getLocaleSelector, getProductKey } from '~/app/store/config'
import { useShowFavorites } from '~/app/features/Gallery/hooks/useShowFavorites'
import { useCallback } from 'react'
import { useIdentity } from '@vp/ubik-context'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { DTT } from 'shared/types/vp/dtt'

type Favorites = DTT.Models.WorkEntityService.WorkEntity<GalleryContentQuery.PreviewInfo>[]

export const useFavoriteId = (entityId: string): string | undefined => {
  const locale = useSelector(getLocaleSelector)
  const { auth, identity } = useIdentity()
  const productKey = useSelector(getProductKey)

  const showFavorites = useShowFavorites()

  const isEnabled = showFavorites && !!auth?.getCanonicalId() && !!identity?.authorizationHeader && !!locale

  const selectFavoriteId = useCallback(
    (favorites: Favorites): string | undefined => favorites.find((favorite) => favorite.design.metadata.designId === entityId)?.workId,
    [entityId],
  )

  return useQuery({
    queryKey: [
      QUERY_KEY,
      {
        locale,
        wesTenant: WES_TENANT,
        ownerId: auth?.getCanonicalId(),
        authorizationHeader: identity?.authorizationHeader,
        productKey,
      },
    ],
    queryFn: queryFavorites,

    enabled: isEnabled,
    retry: 1,
    staleTime: Infinity,
    select: selectFavoriteId,
  }).data
}
